import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CalendarIcon from '@untitled-ui/icons-react/build/esm/CalendarPlus01';
import HomeLine from '@untitled-ui/icons-react/build/esm/HomeLine';

import { Spin } from 'antd';
import { useGetIndividualByIdQuery } from 'api/IndividualsApiSlice';
import { BaseButton, Breadcrumb, StatusTag, TableComponent, useModal } from 'components';
import { format } from 'date-fns';
import { GetPersonModel } from 'solstice-types';
import { formatMobilePhoneForDisplay, getFullName } from 'utils';
import { individualDetailsColumns } from 'utils/constants';
import { StatusChangeModal } from './components/StatusChangeModal';

export const IndividualDetails = () => {
  const { id = '' } = useParams<{ id: string }>();

  const { data: individualData, isLoading } = useGetIndividualByIdQuery(id);

  const {
    firstName,
    lastName,
    status = null,
    email,
    mobilePhone,
    createdAt,
    dateOfBirth,
    middleName,
    address,
    note,
    relatedPersons = [],
  } = individualData ?? {};

  const individualName = getFullName(firstName, lastName);
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();

  const handleDisplayModal = useCallback(
    (record: GetPersonModel | null) => {
      if (!record) return;

      openModal(
        <StatusChangeModal selectedIndividual={record} lastKycId={record.lastKycId ?? ''} />
      );
    },
    [openModal, closeModal]
  );

  if (isLoading) {
    return <Spin fullscreen className="translate-x-16" />;
  }

  const breadcrumbItems = [
    {
      title: <HomeLine className="h-5 w-5 text-gray-500" />,
    },
    {
      title: 'Know Your Customer',
      href: '/know-your-customer',
      className: 'text-gray-600 font-medium',
    },
    {
      title: individualName,
      className: 'font-semibold text-blue-600',
    },
  ];
  const createdAtFormat = createdAt
    ? format(new Date(Date.parse(createdAt)), 'dd/MM/yyyy - hh:mm a')
    : '';
    
  return (
    <div className="pt-8 h-screen">
      <div className="w-full border-b px-5 pb-6">
        <Breadcrumb items={breadcrumbItems} />
        <div className="mt-5 flex justify-between font-bold text-2xl text-gray-800">
          <div className=" flex items-center gap-2.5">
            {individualName}
            <StatusTag status={status} />
          </div>

          <div className="flex gap-2.5">
            <BaseButton
              className="secondary-button"
              onClick={() => navigate(`/entity-details/${id}/audit-log`)}
            >
              View Audit Log
            </BaseButton>
          </div>
        </div>
        <div className="text-gray-600 text-sm font-normal flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-gray-400" />
          <span> Added on: {createdAtFormat} </span>
        </div>
      </div>

      <div className="w-full p-2.5 bg-gray-100 overflow-y-auto h-[calc(100vh-162px)]">
        <div className="w-full p-4 bg-white rounded-lg flex flex-col">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <span className="font-semibold text-base text-gray-600">ID Verification</span>
              <span className="text-gray-600 text-sm pb-5 pt-1">
                Once the verification is complete, you will be able to see the full report here.
                Warning: the verification link is available for 7 days only!
              </span>
            </div>
          </div>

          <TableComponent
            columns={individualDetailsColumns(handleDisplayModal)}
            dataSource={[individualData]}
            hasPagination={false}
          />
        </div>

        <div className="w-full p-4 mt-2.5 bg-white rounded-lg flex flex-col">
          <span className="font-semibold text-base text-gray-600">Individual Info</span>
          <span className="text-gray-600 text-sm pb-5 pt-1">
            Summary information found on this individual.
          </span>

          <div className="bg-gray-50 text-gray-500 p-5 font-normal text-xs rounded-lg h-fit w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <div className="flex gap-2">
                <span className="text-gray-500">First Name</span>
                <span className="font-semibold text-gray-800">{firstName || '-'}</span>
              </div>

              <div className="flex gap-2">
                <span className="text-gray-500">Middle Name</span>
                <span className="font-semibold text-gray-800">
                  {middleName !== '' ? middleName : '-'}
                </span>
              </div>

              <div className="flex gap-2">
                <span className="text-gray-500">Last Name</span>
                <span className="font-semibold text-gray-800">{lastName ?? '-'}</span>
              </div>

              <div className="flex gap-2">
                <span className="text-gray-500">Email</span>
                <span className="font-semibold text-gray-800">{email ?? '-'}</span>
              </div>

              <div className="flex gap-2">
                <span className="text-gray-500">Address</span>
                <span className="font-semibold text-gray-800">{address || '-'}</span>
              </div>

              <div className="flex gap-2">
                <span className="text-gray-500">Phone Number</span>
                <span className="font-semibold text-gray-800">
                  {formatMobilePhoneForDisplay(mobilePhone) ?? '-'}
                </span>
              </div>

              <div className="flex gap-2">
                <span className="text-gray-500">Date of Birth</span>
                <span className="font-semibold text-gray-800">
                  {dateOfBirth && format(new Date(dateOfBirth), 'dd/MM/yyyy') !== '01/01/0001'
                    ? format(new Date(dateOfBirth), 'dd/MM/yyyy')
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {note?.trim() && (
          <div className="w-full p-4 mt-2.5 bg-white rounded-lg flex flex-col">
            <span className="font-semibold text-base text-gray-600">Note</span>
            <span className="text-gray-600 text-sm pb-5 pt-1">
              Notes recorded against this individual.
            </span>

            <div className="bg-gray-50 text-gray-800 p-5 font-normal text-xs rounded-lg h-fit w-full whitespace-pre-wrap break-words">
              {note}
            </div>
          </div>
        )}

        <div className="w-full p-4 mt-2.5 bg-white rounded-lg flex flex-col">
          <span className="font-semibold text-base text-gray-600">Associated Parties</span>
          <span className="text-gray-600 text-sm pb-5 pt-1">
            {relatedPersons && relatedPersons?.length > 0
              ? 'Below is the list and details of all associated parties'
              : 'No associated parties found.'}
          </span>

          {relatedPersons?.map((person, index) => (
            <div key={person.id} className="mb-5">
              <span className="text-gray-600 text-sm font-semibold">Person #{index + 1}</span>
              <div className="bg-gray-50 text-gray-500 p-5 font-normal text-xs rounded-lg h-fit w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  <div className="flex gap-2">
                    <span className="text-gray-500">First Name</span>
                    <span className="font-semibold text-gray-800">{person.firstName || '-'}</span>
                  </div>

                  <div className="flex gap-2">
                    <span className="text-gray-500">Middle Name</span>
                    <span className="font-semibold text-gray-800">
                      {person.middleName !== '' ? person.middleName : '-'}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <span className="text-gray-500">Last Name</span>
                    <span className="font-semibold text-gray-800">{person.lastName || '-'}</span>
                  </div>

                  <div className="flex gap-2">
                    <span className="text-gray-500">Email</span>
                    <span className="font-semibold text-gray-800">{person.email || '-'}</span>
                  </div>

                  <div className="flex gap-2">
                    <span className="text-gray-500">Address</span>
                    <span className="font-semibold text-gray-800">{person.address || '-'}</span>
                  </div>

                  <div className="flex gap-2">
                    <span className="text-gray-500">Phone Number</span>
                    <span className="font-semibold text-gray-800">
                      {formatMobilePhoneForDisplay(person.mobilePhone) ?? '-'}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <span className="text-gray-500">Date of Birth</span>
                    <span className="font-semibold text-gray-800">
                      {person.dateOfBirth &&
                      format(new Date(person.dateOfBirth), 'dd/MM/yyyy') !== '01/01/0001'
                        ? format(new Date(person.dateOfBirth), 'dd/MM/yyyy')
                        : '-'}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <span className="text-gray-500">Relationship</span>
                    <span className="font-semibold text-gray-800">{person.role || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
